/**
 * «ФОТ месяца» у Экономиста: начисленное Хронума по объектам и центрам затрат
 * (ТЗ AI-Economist-2026 ред. 2.1, п. 6.4.5, 6.10). Вкладка видна только с
 * правом на ФОТ `econ.payroll.read` — суммы по должностям это чувствительные
 * данные, и ручка без права отвечает 403.
 *
 * Три вещи, без которых цифру нельзя переносить в БДР, видны на экране сразу:
 * чего в суммах нет (взносы, районный коэффициент, северные — список присылает
 * Хронум), сколько денег не попало в свод (площадки без привязки и люди без
 * площадки) и у каких должностей нет категории ФОТ.
 */
import { useId } from 'react';
import styled from 'styled-components';
import {
  apiLoadErrorText,
  useGetEconChronumLaborCostQuery,
  type ChronumLaborCost,
  type ChronumPayrollCategory,
  type ChronumPayrollObject,
  type ChronumSitePayroll,
  type ChronumSnapshotMeta,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconArrowRight, IconRefresh } from '@shared/ui/icons';
import { ScrollRegion } from '@entities/fleet';
import { fleetLeading, fleetType } from '@entities/fleet';
import {
  MAN_HOURS,
  addDecimals,
  categoryColumns,
  emptyHoursReason,
  formatDateTime,
  formatHours,
  formatInt,
  formatMoney,
  formatMoneyShort,
  isPositiveDecimal,
  notIncludedLabel,
  originLabel,
  outsidePayroll,
  payrollTotals,
  people,
  periodLabel,
  periodLabelLower,
  sortHoursObjects,
  sourceLabel,
  targetKey,
  targetKindLabel,
  targetLabel,
  typo,
  unattributedPayrollReason,
} from '@entities/chronum';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import type { ChronumRefresh } from './useChronumRefresh';
import {
  ArrowLink,
  CellMeta,
  DataTable,
  EmptyBox,
  Eyebrow,
  ListSkeleton,
  NARROW,
  Section,
  SectionHead,
  SectionNote,
  SectionTitle,
} from './ui';

type PayrollData = Extract<ChronumLaborCost, { available: true }>;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const SnapshotLine = styled.p`
  margin: 0;
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
  text-wrap: pretty;
`;

/** Итоги месяца крупными цифрами, через волосяные линии. */
const Figures = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin: ${({ theme }) => `0 0 ${theme.spacing(6)}`};
  border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  & > div {
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing(1)};
    min-width: 0;
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(4)} 0`};
  }

  & > div + div {
    padding-left: ${({ theme }) => theme.spacing(4)};
    border-left: 1px solid ${({ theme }) => theme.colors.border};
  }

  & dd {
    margin: 0;
    font-size: ${fleetType.title};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  & dt {
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > div[data-tone='warning'] dd::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: ${({ theme }) => theme.spacing(2)};
    border-radius: 50%;
    vertical-align: 0.2em;
    background: ${({ theme }) => theme.colors.status.warning};
  }

  @container chronum (max-width: ${NARROW}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > div,
    & > div + div {
      padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(3)} 0`};
      border-left: none;
    }

    & > div:nth-child(even) {
      padding-left: ${({ theme }) => theme.spacing(3)};
      border-left: 1px solid ${({ theme }) => theme.colors.border};
    }

    & > div:nth-child(n + 3) {
      border-top: 1px solid ${({ theme }) => theme.colors.border};
    }

    & dd {
      font-size: ${fleetType.lead};
    }
  }
`;

const Attention = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};

  & > li {
    display: flex;
    align-items: baseline;
    gap: ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
    text-wrap: pretty;
  }

  & > li::before {
    content: '';
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.warning};
    transform: translateY(-1px);
  }
`;

const Calm = styled.p`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  &::before {
    content: '';
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.success};
    transform: translateY(-1px);
  }
`;

interface Props {
  period: string;
  configured: boolean;
  statusLoading: boolean;
  /** Последний снимок начисленного за месяц — для пустого состояния. */
  lastSnapshot: ChronumSnapshotMeta | null;
  categories: readonly ChronumPayrollCategory[];
  canWrite: boolean;
  refresh: ChronumRefresh;
  onOpenSite: (siteId: string) => void;
  onOpenUnmapped: () => void;
}

/** «ФОТ месяца»: начисленное по объектам и центрам затрат. */
export function PayrollPanel(props: Props) {
  const { period } = props;
  const payroll = useGetEconChronumLaborCostQuery(period);
  // currentData — только ответ за выбранный месяц: при смене месяца прошлый
  // свод не показывается под новой подписью.
  const data = payroll.currentData;

  if (payroll.isError) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">
          {apiLoadErrorText(
            payroll.error,
            'Не удалось загрузить начисленное.',
            'Нет права на ФОТ: суммы по должностям видит главный экономист и те, кому выдано право «econ.payroll.read».',
          )}
        </span>
        <TextButton type="button" onClick={() => void payroll.refetch()}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  if (!data) return <PayrollSkeleton />;
  if (!data.available) return <PayrollEmpty {...props} />;
  return <PayrollView {...props} data={data} refetching={payroll.isFetching} />;
}

function PayrollSkeleton() {
  return (
    <div aria-busy="true" aria-label="Загрузка начисленного">
      <SkeletonLine $height={44} $radius="10px" />
      <SkeletonLine $height={12} $width="50%" />
      <div>&nbsp;</div>
      <SkeletonLine $height={64} />
      <div>&nbsp;</div>
      <ListSkeleton rows={6} label="Загрузка таблицы начисленного" />
    </div>
  );
}

function PayrollEmpty({ period, configured, statusLoading, lastSnapshot, canWrite, refresh }: Props) {
  const reason = emptyHoursReason(lastSnapshot, configured || statusLoading);
  const busy = refresh.running && refresh.runningPeriod === period;
  return (
    <EmptyBox>
      <Eyebrow>{periodLabel(period)}</Eyebrow>
      <h3>{reason.title}</h3>
      <p>{reason.text}</p>
      {canWrite && reason.canRefresh && configured && (
        <div className="actions">
          <Button
            type="button"
            $variant="primary"
            disabled={refresh.starting || busy}
            onClick={() => refresh.run(period)}
          >
            <IconRefresh size={16} />
            {busy ? 'Загружаю…' : 'Обновить'}
          </Button>
        </div>
      )}
    </EmptyBox>
  );
}

function PayrollView({
  data,
  refetching,
  period,
  categories,
  onOpenSite,
  onOpenUnmapped,
}: Props & { data: PayrollData; refetching: boolean }) {
  const figuresId = useId();
  const attentionId = useId();
  const objectsId = useId();
  const rowsId = useId();

  const totals = payrollTotals(data.objects);
  const outside = outsidePayroll(data.anomalies);
  const source = sourceLabel(data.source);
  const anyMoney = data.objects.length > 0 || isPositiveDecimal(outside.total);

  return (
    <div aria-busy={refetching}>
      <Stack>
        {data.not_included.length > 0 && (
          <Notice $tone="warning">
            <span className="text">
              <strong>Это не полный ФОТ.</strong>{' '}
              {typo(
                `В суммах Хронума нет: ${data.not_included.map(notIncludedLabel).join(', ')}. Для затрат БДР эту часть добирают из 1С или просят Хронум считать с коэффициентами и взносами.`,
              )}
            </span>
          </Notice>
        )}
        <SnapshotLine>
          {`Снимок начисленного за ${periodLabelLower(period)} — ${formatDateTime(data.snapshot.fetched_at)}, ${originLabel(data.snapshot)}.`}
          {source ? ` Источник: ${source}.` : ''}
          {data.generated_at ? ` Хронум посчитал ${formatDateTime(data.generated_at)}.` : ''}
          {data.status === 'open'
            ? ' Месяц в Хронуме не закрыт — суммы ещё изменятся.'
            : ''}
        </SnapshotLine>
      </Stack>

      {!anyMoney ? (
        <EmptyBox>
          <h3>Начисленного за месяц нет</h3>
          <p>
            {typo(
              `Снимок за ${periodLabelLower(period)} получен, но сумм в нём нет. Если зарплата за месяц считалась, проверьте расчёт в Хронуме и обновите снимок.`,
            )}
          </p>
        </EmptyBox>
      ) : (
        <>
          <Figures aria-labelledby={figuresId}>
            <div>
              <dt id={figuresId}>начислено по объектам и центрам затрат</dt>
              <dd>{formatMoneyShort(totals.accrued)}</dd>
            </div>
            <div>
              <dt>{typo(`${MAN_HOURS}, по которым считалась оплата`)}</dt>
              <dd>{formatHours(totals.hours)}</dd>
            </div>
            <div>
              <dt>{typo('человек — сумма по площадкам')}</dt>
              <dd>{formatInt(totals.workersSumBySites)}</dd>
            </div>
            {isPositiveDecimal(outside.total) && (
              <div data-tone="warning">
                <dt>{typo('не вошло в свод: вне объектов и без площадки')}</dt>
                <dd>{formatMoneyShort(outside.total)}</dd>
              </div>
            )}
          </Figures>

          <Section aria-labelledby={attentionId}>
            <SectionHead>
              <SectionTitle id={attentionId}>Что мешает разнести по БДР</SectionTitle>
            </SectionHead>
            <AttentionList
              data={data}
              outside={outside}
              onOpenSite={onOpenSite}
              onOpenUnmapped={onOpenUnmapped}
            />
          </Section>

          <Section aria-labelledby={objectsId}>
            <SectionHead>
              <SectionTitle id={objectsId}>Начисленное по объектам</SectionTitle>
              <SectionNote>{typo('по категориям ФОТ — строки 37–41 «БДР сводной»')}</SectionNote>
            </SectionHead>
            {data.objects.length > 0 ? (
              <ObjectsTable objects={data.objects} categories={categories} labelledBy={objectsId} />
            ) : (
              <SectionNote>
                {typo(
                  'Ни одна площадка с начислениями не привязана к объекту или центру затрат. Привяжите площадки на вкладке «Площадки».',
                )}
              </SectionNote>
            )}
          </Section>

          <Section aria-labelledby={rowsId}>
            <SectionHead>
              <SectionTitle id={rowsId}>По должностям</SectionTitle>
              <SectionNote>
                {typo('как отдал Хронум; «Прочие» — где людей меньше порога и должность скрыта')}
              </SectionNote>
            </SectionHead>
            <RowsTable data={data} labelledBy={rowsId} />
          </Section>
        </>
      )}
    </div>
  );
}

function AttentionList({
  data,
  outside,
  onOpenSite,
  onOpenUnmapped,
}: {
  data: PayrollData;
  outside: ReturnType<typeof outsidePayroll>;
  onOpenSite: (siteId: string) => void;
  onOpenUnmapped: () => void;
}) {
  const a = data.anomalies;
  const items: { key: string; node: React.ReactNode }[] = [];

  const siteItem = (s: ChronumSitePayroll, conflict: boolean) => ({
    key: `${conflict ? 'conflict' : 'unbound'}:${s.site_id}`,
    node: (
      <>
        {typo(
          conflict
            ? `«${s.site_name}» — ${formatMoney(s.accrued)} ₽ (${people(s.workers)}): у площадки две привязки сразу, её деньги не в своде.`
            : `«${s.site_name}» — ${formatMoney(s.accrued)} ₽ (${people(s.workers)}): площадка не привязана ни к объекту, ни к центру затрат.`,
        )}{' '}
        <TextButton type="button" onClick={() => onOpenSite(s.site_id)}>
          Привязка площадки
        </TextButton>
      </>
    ),
  });
  a.conflicting_sites.forEach((s) => items.push(siteItem(s, true)));
  a.unbound_sites.forEach((s) => items.push(siteItem(s, false)));

  a.unattributed.forEach((u, i) =>
    items.push({
      key: `unattributed:${u.reason}:${i}`,
      node: typo(
        `Без площадки — ${formatMoney(u.accrued)} ₽ (${people(u.workers)}): ${unattributedPayrollReason(u.reason)}. По объектам такие деньги раскладывает правило экономиста, ОСНОВА их не делит.`,
      ),
    }),
  );

  if (a.positions_without_category.length > 0) {
    const sum = addDecimals(a.positions_without_category.map((p) => p.accrued));
    const top = [...a.positions_without_category].sort(
      (x, y) => Number(y.accrued) - Number(x.accrued),
    )[0];
    items.push({
      key: 'unmapped',
      node: (
        <>
          {typo(
            `Должностей без категории ФОТ — ${a.positions_without_category.length} на ${formatMoney(sum)} ₽ (крупнейшая «${top.position_name}»): их деньги идут в колонку «Без категории», а не в строку БДР.`,
          )}{' '}
          <TextButton type="button" onClick={() => onOpenUnmapped()}>
            Задать категории
          </TextButton>
        </>
      ),
    });
  }

  if (items.length === 0) {
    return (
      <Calm>
        {typo(
          'Всё разнесено: каждая площадка с начислениями привязана к одному объекту или центру затрат, у всех должностей есть категория ФОТ, денег без площадки Хронум не прислал.',
        )}
      </Calm>
    );
  }
  return (
    <>
      <Attention>
        {items.map((i) => (
          <li key={i.key}>{i.node}</li>
        ))}
      </Attention>
      <SectionNote>
        {typo(
          `Вне свода — ${formatMoney(outside.total)} ₽ из ${formatMoney(
            addDecimals([outside.total, payrollTotals(data.objects).accrued]),
          )} ₽ начисленного за месяц.`,
        )}
      </SectionNote>
    </>
  );
}

function ObjectsTable({
  objects,
  categories,
  labelledBy,
}: {
  objects: readonly ChronumPayrollObject[];
  categories: readonly ChronumPayrollCategory[];
  labelledBy: string;
}) {
  const sorted = sortHoursObjects(objects);
  const cols = categoryColumns(categories, objects);
  const totals = payrollTotals(objects);
  return (
    <ScrollRegion label="Начисленное по объектам — таблица">
      <DataTable aria-labelledby={labelledBy}>
        <thead>
          <tr>
            <th scope="col">Объект или центр затрат</th>
            {cols.map((c) => (
              <th key={c.code} scope="col" className="num">
                {c.title}
              </th>
            ))}
            <th scope="col" className="num">
              Начислено, ₽
            </th>
            <th scope="col" className="num">
              {MAN_HOURS}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((o) => (
            <tr key={targetKey(o.target)}>
              <th scope="row">
                <span>{targetLabel(o.target)}</span>
                <CellMeta>
                  {targetKindLabel(o.target)} · {o.sites.map((s) => s.name).join(', ')}
                </CellMeta>
              </th>
              {cols.map((c) => {
                const v = o.by_category[c.code];
                return (
                  <td key={c.code} className={`num${isPositiveDecimal(v) ? '' : ' empty'}`}>
                    <span className="narrow-only">{c.title}</span>
                    {isPositiveDecimal(v) ? formatMoney(v) : '—'}
                  </td>
                );
              })}
              <td className="num">
                <span className="narrow-only">Начислено, ₽</span>
                {formatMoney(o.accrued)}
              </td>
              <td className="num">
                <span className="narrow-only">{MAN_HOURS}</span>
                {formatHours(o.hours)}
              </td>
            </tr>
          ))}
        </tbody>
        {sorted.length > 1 && (
          <tfoot>
            <tr>
              <th scope="row">Итого по объектам и центрам затрат</th>
              {cols.map((c) => (
                <td key={c.code} className="num">
                  <span className="narrow-only">{c.title}</span>
                  {formatMoney(totals.byCategory[c.code] ?? null)}
                </td>
              ))}
              <td className="num">
                <span className="narrow-only">Начислено, ₽</span>
                {formatMoney(totals.accrued)}
              </td>
              <td className="num">
                <span className="narrow-only">{MAN_HOURS}</span>
                {formatHours(totals.hours)}
              </td>
            </tr>
          </tfoot>
        )}
      </DataTable>
    </ScrollRegion>
  );
}

function RowsTable({ data, labelledBy }: { data: PayrollData; labelledBy: string }) {
  // Крупные суммы сверху: экономист сверяет сначала то, что весит.
  const rows = [...data.rows].sort((a, b) => Number(b.accrued ?? 0) - Number(a.accrued ?? 0));
  return (
    <ScrollRegion label="Начисленное по должностям — таблица">
      <DataTable aria-labelledby={labelledBy}>
        <thead>
          <tr>
            <th scope="col">Должность</th>
            <th scope="col">Площадка</th>
            <th scope="col" className="num">
              Человек
            </th>
            <th scope="col" className="num">
              {MAN_HOURS}
            </th>
            <th scope="col" className="num">
              Начислено, ₽
            </th>
            <th scope="col" className="num">
              Удержано, ₽
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.site.id}:${r.position?.key ?? r.label ?? i}`}>
              <th scope="row">{r.position?.name ?? r.label ?? 'Без должности'}</th>
              <td>{r.site.name}</td>
              <td className="num">
                <span className="narrow-only">Человек</span>
                {formatInt(r.workers)}
              </td>
              <td className="num">
                <span className="narrow-only">{MAN_HOURS}</span>
                {formatHours(r.hours)}
              </td>
              <td className="num">
                <span className="narrow-only">Начислено, ₽</span>
                {formatMoney(r.accrued)}
              </td>
              <td className={`num${r.deductions ? '' : ' empty'}`}>
                <span className="narrow-only">Удержано, ₽</span>
                {r.deductions ? formatMoney(r.deductions) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      <SectionNote>
        {typo('Начисленное по должностям — агрегаты без ФИО: так требует право на ФОТ.')}{' '}
        <ArrowLink to="/agents/econ/chronum?tab=positions">
          Категории ФОТ должностей
          <IconArrowRight size={14} />
        </ArrowLink>
      </SectionNote>
    </ScrollRegion>
  );
}
